import { useCallback, useEffect, useRef, useState } from 'react';
import { CURRENT_RELEASE, getUpdateNoticeStorageKey } from '@/config/releaseNotes';
import { useAuth } from '@/hooks/useAuth';

export default function UpdateNoticeModal() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setIsOpen(false);
      return;
    }

    const storageKey = getUpdateNoticeStorageKey(user.id);
    try {
      if (window.localStorage.getItem(storageKey) === CURRENT_RELEASE.version) {
        return;
      }
    } catch {
      // 브라우저 저장소를 사용할 수 없어도 이번 접속에서는 팝업을 보여줍니다.
    }

    const timer = window.setTimeout(() => setIsOpen(true), 350);
    return () => window.clearTimeout(timer);
  }, [user?.id]);

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => modalRef.current?.focus(), 50);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  const closeNotice = useCallback(() => {
    if (user?.id) {
      try {
        window.localStorage.setItem(
          getUpdateNoticeStorageKey(user.id),
          CURRENT_RELEASE.version,
        );
      } catch {
        // 저장소가 차단된 브라우저에서는 다음 접속 때 다시 표시될 수 있습니다.
      }
    }
    setIsOpen(false);
  }, [user?.id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNotice();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeNotice]);

  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) closeNotice();
  }, [closeNotice]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center px-0 sm:px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-notice-title"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/45 animate-fade-in" />

      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full sm:max-w-md overflow-hidden rounded-t-3xl sm:rounded-3xl bg-background-50 shadow-2xl animate-slide-up sm:animate-scale-in"
      >
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="h-1 w-10 rounded-full bg-foreground-200" />
        </div>

        <div className="px-6 pt-4 sm:pt-6 pb-3 flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-700">
              <i className="ri-sparkling-2-line" />
              업데이트 소식
            </div>
            <h2 id="update-notice-title" className="mt-3 text-xl font-heading font-bold text-foreground-950">
              {CURRENT_RELEASE.title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-foreground-500">
              {CURRENT_RELEASE.description}
            </p>
          </div>
          <button
            type="button"
            onClick={closeNotice}
            className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-secondary-100"
            aria-label="업데이트 안내 닫기"
          >
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>

        <div className="mx-6 rounded-2xl border border-accent-100 bg-accent-50/60 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-foreground-900">Family Album v{CURRENT_RELEASE.version}</span>
            <span className="text-xs text-foreground-500">{CURRENT_RELEASE.date}</span>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {CURRENT_RELEASE.changes.map((change) => (
            <div key={change.title} className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-secondary-100">
                <i className={`${change.icon} text-lg text-accent-600`} />
              </div>
              <div className="pt-0.5">
                <h3 className="text-sm font-semibold text-foreground-900">{change.title}</h3>
                <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">{change.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={closeNotice}
            className="w-full rounded-xl bg-accent-500 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-600"
          >
            확인했어요
          </button>
          <p className="mt-3 text-center text-[11px] text-foreground-400">
            이 안내는 현재 버전에서 한 번만 표시됩니다.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-scale-in { animation: scale-in 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
      `}</style>
    </div>
  );
}
